// ─────────────────────────────────────────────────────────────
// Trips Service — CRUD de viagens
// ─────────────────────────────────────────────────────────────
import { ref, set, get, remove, onValue, off } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage }       from '@/firebase';
import { toastError }        from '@/ui/toast';
import { uid }               from '@/helpers';
import type { Trip }         from '@/types';

// ── Load all trips for current user ──────────────────────────
export async function loadUserTrips(userUid: string): Promise<Trip[]> {
  try {
  const indexSnap = await get(ref(db, `userTrips/${userUid}`));
  const index     = indexSnap.val() as Record<string, boolean> | null;
  if (!index) return [];

  const ids = Object.keys(index);
  const snapshots = await Promise.all(ids.map(id => get(ref(db, `trips/${id}`))));
  return snapshots
    .filter(s => s.exists())
    .map(s => ({ id: s.key!, ...s.val() } as Trip));
  } catch (e) {
    toastError('Erro ao carregar viagens: ' + (e instanceof Error ? e.message : String(e)));
    return [];
  }
}

// ── Subscribe to single trip (realtime) ──────────────────────
export function subscribeTrip(
  tripId:   string,
  onChange: (trip: Trip) => void
): () => void {
  const tripRef = ref(db, `trips/${tripId}`);
  onValue(tripRef, snap => {
    if (snap.exists()) onChange({ id: tripId, ...snap.val() } as Trip);
  });
  return () => off(tripRef);
}

// ── Create or update trip ─────────────────────────────────────
export async function saveTrip(
  tripData:     Partial<Trip>,
  userUid:      string,
  coverFile?:   File,
  onProgress?:  (pct: number) => void
): Promise<Trip> {
  const id       = tripData.id ?? uid();
  let   coverUrl = tripData.coverUrl ?? '';
  let   coverPath= tripData.coverPath ?? '';

  // Upload cover image if provided
  if (coverFile) {
    coverPath = `covers/${id}/${coverFile.name}`;
    const sRef  = storageRef(storage, coverPath);
    const task  = uploadBytesResumable(sRef, coverFile);

    await new Promise<void>((resolve, reject) => {
      task.on('state_changed',
        snap => onProgress?.(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
        reject,
        resolve
      );
    });
    coverUrl = await getDownloadURL(sRef);
  }

  const obj: Trip = {
    id,
    name:        tripData.name        ?? '',
    start:       tripData.start       ?? '',
    end:         tripData.end         ?? '',
    emoji:       tripData.emoji       ?? '✈️',
    color:       tripData.color       ?? '#00A3E0',
    coverUrl,
    coverPath,
    budgetLimit: tripData.budgetLimit ?? 0,
    currencies:  tripData.currencies  ?? ['BRL'],
    travelers:   tripData.travelers   ?? {},
    destinos:    tripData.destinos    ?? {},
    transporte:  tripData.transporte  ?? {},
    hoteis:      tripData.hoteis      ?? {},
    agenda:      tripData.agenda      ?? {},
    checks:      tripData.checks      ?? {},
    cotacoes:    tripData.cotacoes    ?? {},
    compras:     tripData.compras     ?? {},
    docs:        tripData.docs        ?? {},
    expenses:    tripData.expenses    ?? {},
  };

  const updates: Record<string, unknown> = {};
  updates[`trips/${id}`]                   = obj;
  updates[`userTrips/${userUid}/${id}`]    = true;

  // Also link all travelers
  Object.values(obj.travelers).forEach(tr => {
    if (tr.uid) updates[`userTrips/${tr.uid}/${id}`] = true;
  });

  await set(ref(db), updates as never);
  return obj;
}

// ── Delete trip ───────────────────────────────────────────────
export async function deleteTrip(tripId: string, userUid: string): Promise<void> {
  // Delete trips data FIRST (while userTrips still exists and grants permission)
  // then remove the userTrips index entry
  await remove(ref(db, `trips/${tripId}`));
  await remove(ref(db, `userTrips/${userUid}/${tripId}`));
}

// ── Upload attachment ─────────────────────────────────────────
export async function uploadAttachment(
  tripId:      string,
  section:     string,
  itemId:      string,
  file:        File,
  onProgress?: (pct: number) => void
): Promise<{ id: string; name: string; url: string; path: string }> {
  const attachId  = uid();
  const path      = `attachments/${tripId}/${section}/${itemId}/${attachId}_${file.name}`;
  const sRef      = storageRef(storage, path);
  const task      = uploadBytesResumable(sRef, file);

  await new Promise<void>((resolve, reject) => {
    task.on('state_changed',
      snap => onProgress?.(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      reject,
      resolve
    );
  });

  const url = await getDownloadURL(sRef);
  return { id: attachId, name: file.name, url, path };
}

// ── Delete attachment ─────────────────────────────────────────
export async function deleteAttachment(path: string): Promise<void> {
  try { await deleteObject(storageRef(storage, path)); }
  catch { /* file may already be gone */ }
}

// ── Delete field inside trip ──────────────────────────────────
export async function deleteTripField(tripId: string, path: string): Promise<void> {
  await remove(ref(db, `trips/${tripId}/${path}`));
}
